import Utils from "@/core/utils/Utils";
import DateUtils from "@/core/utils/DateUtils";
import TimeZone from "@/domains/TimeZones/TimeZone";

/**
 * Расширенный класс Date с дополнительными методами для работы с датами
 */
class TimeStamp extends Date {

  /**
   * Создаёт дату с учётом таймзоны пользователя.
   *
   * По умолчанию применяет офсет между браузерной и пользовательской таймзоной.
   * Для дат с сервера (уже в правильной таймзоне) передать `false` последним аргументом.
   *
   * @param args - Аргументы как у стандартного Date, плюс опциональный флаг isApplyOffset
   *
   * @example
   * // Текущее время в таймзоне пользователя
   * new TimeStamp()
   *
   * @example
   * // Конкретная дата в таймзоне пользователя
   * new TimeStamp('2024-03-01')
   * new TimeStamp(2024, 2, 1)
   * new TimeStamp(2024, 2, 1, 12, 30)
   *
   * @example
   * // Дата с сервера — офсет не применяется
   * new TimeStamp(serverResponse.date, false)
   * new TimeStamp('2024-03-01T12:00:00', false)
   */
  constructor(
      ...args:
          | []
          | [value: string | number | Date | TimeStamp, isApplyOffset?: boolean]
          | [...ConstructorParameters<typeof Date>, isApplyOffset?: boolean]
  ) {
    // Проверяем, передан ли флаг isApplyOffset последним аргументом
    const last = args[args.length - 1];
    const isApplyOffset = typeof last === 'boolean' ? args.pop() as boolean : true;

    // Вызываем родительский конструктор с оставшимися аргументами
    if (args.length === 0) {
      super();
    } else {
      super(...args as ConstructorParameters<typeof Date>);
    }

    // Применяем офсет, если требуется
    if (isApplyOffset) {
      this.setHours(this.getHours() + TimeZone.getBrowserToUserOffsetDiff())
    }
  }


  static fromServer(data: Date): TimeStamp | null;
  /** Создать TimeStamp из объекта бэкенда
   *       "createdAt": {
   *         "date": "2025-03-26 20:51:19.818168",
   *         "timezone_type": 1,
   *         "timezone": "+07:00"
   *       },
   * */
  static fromServer(data: { date: string; timezone_type: number; timezone: string; }): TimeStamp | null;
  /**
   * Создать TimeStamp из данных сервера или Date объекта
   *
   * @param data - Объект с бэкенда или Date объект
   * @returns TimeStamp экземпляр или null если невалидные данные
   *
   * @example
   * // Из объекта бэкенда
   * TimeStamp.fromServer({
   *   date: "2025-03-26 20:51:19.818168",
   *   timezone_type: 1,
   *   timezone: "+07:00"
   * });
   *
   * @example
   * // Из Date объекта
   * TimeStamp.fromServer(new Date());
   */
  static fromServer(data: any): TimeStamp | null {
    if (!data) { return null; }

    // Если это Date объект
    if (data instanceof Date) {
      const dateTime = new TimeStamp(data.toISOString() as any, false);
      return isNaN(dateTime.getTime()) ? null : dateTime;
    }

    // Проверяем что это дата с бэкенда
    if (
        typeof data !== 'object'
        || !data.hasOwnProperty('date')
        || !data.hasOwnProperty('timezone')
        || !data.hasOwnProperty('timezone_type')  // ← snake_case!
    ) {
      return null;
    }

    // Формируем ISO строку
    let isoString = data.date
        .split('.')[0]       // Убираем микросекунды: "2025-11-28 17:00:00.000000" → "2025-11-28 17:00:00"
        .replace(' ', 'T');  // Пробел на T: "2025-11-28 17:00:00" → "2025-11-28T17:00:00"

    // Добавляем timezone если есть
    if (data.timezone) {
      if (data.timezone === 'UTC') {
        isoString += 'Z';  // UTC формат
      } else if (data.timezone_type === 1) {
        // UPDATE 23.11.2025. Чтобы отображать время в таймзоне пользователя,
        // мы не должны присваивать таймзону из ответа сервера
        // Оставляем весь offset (+07:00, а не только +07)
        // isoString += data.timezone;  // "2025-11-28T17:00:00+07:00"
      }
    }

    const date = new TimeStamp(isoString, false as any);

    return isNaN(date.getTime()) ? null: date;
  }

  /**
   * Форматирует дату в ISO-подобный формат
   *
   * @param includeTimezone - Включать ли информацию о таймзоне (по умолчанию true)
   * @returns Строка в формате YYYY-MM-DDTHH:mm:ss.SSS[±HHMM]
   *
   * @example
   * const dt = new TimeStamp('2025-03-26T20:51:19');
   * dt.toISOString(); // "2025-03-26T20:51:19.000+07:00"
   * dt.toISOString(false); // "2025-03-26T20:51:19.000"
   */
  toISOString(includeTimezone = true) {
    return Utils.date.asISOString(this, includeTimezone) as string;
  }

  /**
   * Возвращает строковое представление даты в указанной временной зоне
   *
   * @param {string} timezone - Временная зона в формате IANA (например, 'Europe/Moscow', 'UTC', 'Asia/Tashkent')
   * @returns {string} Отформатированная строка даты в формате 'ДД.МM.ГГГГ, ЧЧ:ММ:СС'
   *
   * @example
   * const date = new TimeStamp();
   * date.toTimezone('Europe/Moscow'); // '10.11.2025, 16:30:45'
   * date.toTimezone('UTC'); // '10.11.2025, 13:30:45'
   */
  toTimezone(timezone: string): string {
    return this.toLocaleString('ru-RU', {
      timeZone: timezone,
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Возвращает строковое представление даты
   * Формат: "ДД.ММ ЧЧ:ММ" если текущий год, иначе "ДД.ММ.ГГГГ ЧЧ:ММ"
   *
   * @returns Отформатированная строка
   *
   * @example
   * // Текущий год
   * const dt = new TimeStamp('2025-03-26T20:51:19');
   * dt.toString(); // "26.03 20:51"
   *
   * @example
   * // Другой год
   * const dt = new TimeStamp('2024-03-26T20:51:19');
   * dt.toString(); // "26.03.2024 20:51"
   *
   * @example
   * // Будущий год
   * const dt = new TimeStamp('2026-12-31T23:59:59');
   * dt.toString(); // "31.12.2026 23:59"
   */
  toString(): string {
    const currentYear = new Date().getFullYear();
    const dateYear = this.getFullYear();

    // Если год совпадает с текущим - не показываем год
    if (dateYear === currentYear) {
      return DateUtils.format(this,'DD|MM|HH|mm') as string;
    }

    // Если год отличается - показываем год
    return DateUtils.format(this, 'DD|MM|YYYY|HH|mm') as string;
  }

  /**
   * Сравнивает TimeStamp объекты
   *
   * @param other - Другая дата
   * @returns -1 если раньше, 0 если равны, 1 если позже
   *
   * @example
   * const dt1 = new TimeStamp('2025-03-26');
   * const dt2 = new TimeStamp('2025-03-27');
   * dt1.compareTo(dt2); // -1
   * dt2.compareTo(dt1); // 1
   * dt1.compareTo(dt1); // 0
   */
  compareTo(other: TimeStamp | Date): number {
    return Utils.date.compareDates(this, other) as number;
  }


  /**
   * Проверяет равенство дат
   *
   * @param other - Другая дата
   * @returns true если даты равны
   *
   * @example
   * const dt1 = new TimeStamp('2025-03-26T20:00:00');
   * const dt2 = new TimeStamp('2025-03-26T20:00:00');
   * dt1.equals(dt2); // true
   */
  equals(other: TimeStamp | Date): boolean {
    return this.compareTo(other) === 0;
  }

  /**
   * Проверяет, позже ли текущая дата
   *
   * @param other - Другая дата
   * @returns true если текущая дата позже
   *
   * @example
   * const dt1 = new TimeStamp('2025-03-27');
   * const dt2 = new TimeStamp('2025-03-26');
   * dt1.isAfter(dt2); // true
   */
  isAfter(other: TimeStamp | Date): boolean {
    return this.compareTo(other) > 0;
  }

  /**
   * Проверяет, раньше ли текущая дата
   *
   * @param other - Другая дата
   * @returns true если текущая дата раньше
   *
   * @example
   * const dt1 = new TimeStamp('2025-03-26');
   * const dt2 = new TimeStamp('2025-03-27');
   * dt1.isBefore(dt2); // true
   */
  isBefore(other: TimeStamp | Date): boolean {
    return this.compareTo(other) < 0;
  }

  /**
   * Добавить дни к дате
   *
   * @param days - Количество дней (может быть отрицательным)
   * @returns Новый экземпляр TimeStamp
   *
   * @example
   * const dt = new TimeStamp('2025-03-26');
   * dt.addDays(5); // 2025-03-31
   * dt.addDays(-5); // 2025-03-21
   */
  addDays(days: number): TimeStamp {
    const newDate = new TimeStamp(this, false);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  }

  /**
   * Добавить часы к дате
   *
   * @param hours - Количество часов (может быть отрицательным)
   * @returns Новый экземпляр TimeStamp
   *
   * @example
   * const dt = new TimeStamp('2025-03-26T20:00:00');
   * dt.addHours(3); // 2025-03-26T23:00:00
   * dt.addHours(-2); // 2025-03-26T18:00:00
   */
  addHours(hours: number) {
    const result = new TimeStamp(this, false); // Простое клонирование
    result.setHours(result.getHours() + hours);
    return result;
  }


  /**
   * Добавить минуты к дате
   *
   * @param minutes - Количество минут (может быть отрицательным)
   * @returns Новый экземпляр TimeStamp
   *
   * @example
   * const dt = new TimeStamp('2025-03-26T20:00:00');
   * dt.addMinutes(30); // 2025-03-26T20:30:00
   * dt.addMinutes(-15); // 2025-03-26T19:45:00
   */
  addMinutes(minutes: number): TimeStamp {
    const result = new TimeStamp(this, false); // Простое клонирование
    result.setMinutes(result.getMinutes() + minutes);
    return result;
  }

  /**
   * Добавить миллисекунды к дате
   *
   * @param milliseconds - Количество миллисекунд (может быть отрицательным)
   * @returns Новый экземпляр TimeStamp
   *
   * @example
   * const dt = new TimeStamp('2025-03-26T20:00:00.000');
   * dt.addMilliseconds(500); // 2025-03-26T20:00:00.500
   */
  addMilliseconds(milliseconds: number): TimeStamp {
    const result = new TimeStamp(this, false); // Простое клонирование
    result.setMilliseconds(result.getMilliseconds() + milliseconds);
    return result;
  }

  /**
   * Проверить находится ли дата в прошлом
   */
  isPast(): boolean {
    return this.getTime() < Date.now();
  }

  /**
   * Проверить находится ли дата в будущем
   */
  isFuture(): boolean {
    return this.getTime() > Date.now();
  }

  /**
   * Возвращает JSON представление
   *
   * @returns ISO строка с таймзоной
   *
   * @example
   * const dt = new TimeStamp('2025-03-26T20:51:19');
   * JSON.stringify({ date: dt }); // {"date":"2025-03-26T20:51:19.000+07:00"}
   */
  toJSON(): string {
    return this.toISOString();
  }

  /**
   * Создаёт глубокую копию TimeStamp без повторного применения офсета.
   *
   * Копирует точное время в миллисекундах, сохраняя уже применённый офсет.
   *
   * @returns Новый экземпляр TimeStamp с тем же временем
   *
   * @example
   * const original = new TimeStamp('2024-03-01'); // С офсетом
   * const cloned = original.cloneDeep(); // Копия с тем же временем
   */
  cloneDeep(): TimeStamp {
    return new TimeStamp(this.getTime(), false);
  }

  /**
   * Создает новый TimeStamp из строки или другой даты
   *
   * @param value - Значение для создания даты
   * @returns Новый экземпляр TimeStamp
   *
   * @example
   * TimeStamp.from('2025-03-26');
   * TimeStamp.from(new Date());
   * TimeStamp.from(1234567890000);
   */
  static from(value: string | Date | number): TimeStamp {
    return new TimeStamp(value, false);
  }

  /**
   * Возвращает текущую дату как Number(TimeStamp)
   *
   * @returns Новый экземпляр с текущим временем
   *
   * @example
   * const now = TimeStamp.now();
   */
  static now(): number {
    return Number(new TimeStamp());
  }

  format(format: string = '') {
    return DateUtils.format(this, format);
  }
}

export default TimeStamp;
